/* eslint-disable max-lines */
import React, {useCallback, useMemo, useState} from "react";
import Textarea from "../../ui/textarea";
import {useFormState} from "../../hooks/useFormState";
import Button from "../../ui/button";
import {useTranslation} from "react-i18next";
import Select from "../../ui/select";
import {Project, Challenge, ChallengeProject} from "../../schema";
import {gql} from "@apollo/client";
import classes from "./ParticipationsActivityModal.module.css";
import DatePicker from "../../ui/datepicker";
import {dateToMoscowTimestamp, getCurrentMoscowTime} from "../../time";
import {HelpCircleIcon} from "../../ui/icons";
import WithTooltip from "../../ui/tooltip";

type PickedChallenge = Pick<Challenge, "id" | "name">;
type PickedProject = Pick<Project, "id" | "name"> & {
  challenges: Pick<Challenge, "id">[]
}
type PickedChallengeProject = PickedChallenge | PickedProject

type contentProps = React.ComponentProps<"div"> &
  (
    | {
        initialChallengeId?: undefined;
        initialAction?: undefined;
        initialType?: undefined;
      }
    | {
        initialChallengeId: "no";
        initialAction: "activate" | "deactivate";
        initialType: ChallengeProject["__typename"],
      }
  ) & {
    challenges?: PickedChallenge[];
    projects?: PickedProject[];
    params: {
      onAssign?: (emails: string[], action: boolean, challengeId?: string,
         projectId?: string, deactivateAt?: number) => void;
      onDelete?: (emails: string[], challengeId?: string, projectId?: string) => void;
    };
  };

function ParticipationActivityModalHeader() {
  const {t} = useTranslation();

  return <span>{t("components.ParticipationActivityModal.title")}</span>;
}

function ParticipationActivityModalContent({challenges, projects, initialType, params}: contentProps) {
  const {t} = useTranslation();

  const formState = useFormState<{
    emails: string;
    id?: string | "no";
    action: "activate" | "deactivate" | "delete";
    type: NonNullable<ChallengeProject["__typename"]>,
  }>({
    initialValues: {
      emails: "",
      id: "no",
      action: "deactivate",
      type: initialType ?? "Challenge",
    },

    preventDefault: true,
    onSubmit: (data) => {
      const emails = data.emails
        .split(/[ ,;\n]/)
        .map((email) => email.trim())
        .filter((email) => !!email);

      if (!emails.length) {
        formState.setValues({
          emails: "",
        });
        return;
      }

      if (data.id && data.id !== "no") {
        switch (data.action) {
          case "activate":
          case "deactivate":
            if (data.type === "Challenge") {
              params.onAssign && params.onAssign(
                emails,
                data.action === "activate",
                data.id,
                undefined,
                deactivateAt && dateToMoscowTimestamp(deactivateAt)
              );
            } else if (data.type === "Project") {
              params.onAssign && params.onAssign(
                emails,
                data.action === "activate",
                undefined,
                data.id,
                deactivateAt && dateToMoscowTimestamp(deactivateAt)
              );
            }
            break;
          case "delete":
            if (data.type === "Challenge") {
              params.onDelete && params.onDelete(emails, data.id, undefined);
            } else if (data.type === "Project") {
              params.onDelete && params.onDelete(emails, undefined, data.id);
            }
            break;
          default:
            console.error(`Unsupported action "${data.action}" in ParticipationsActivityModal`);
        }
      }
    },
  });

  const {type, id: currentId} = formState.values;

  const onTypeChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>((e) => {
    formState.setValues({id: "no"})
    formState.changeHandler(e)
  }, [formState])

  const onActionChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>(
    (e) => {
      formState.changeHandler(e);
    },
    [formState],
  );

  const currentItems = useMemo<typeof challenges | typeof projects>(() => {
          switch (type) {
      case "Challenge":
        return challenges;
      case "Project":
        return projects;
      default:
        console.error(`Unsupported type of item to assign in AssignModal: "${type}"`)
    }
    }
  , [challenges, projects, type])

  const selectedItem = useMemo<PickedChallengeProject | undefined>(() => (
    currentId === "no"
      ? undefined
      : currentItems?.find(({id}) => id === currentId) as PickedChallengeProject | undefined
  ), [currentItems, currentId])

  const isCurrentProjectEmpty = useMemo(() => (
    selectedItem && ("challenges" in selectedItem) ? (selectedItem.challenges.length === 0) : false
  ), [selectedItem])

  const nowDate = new Date();
  const [deactivateAt, setDeactivateAt] = useState<Date | undefined>(undefined);

  const filterPassedTime = (time: Date) => {
    const moscowTime = getCurrentMoscowTime();
    if (time && moscowTime) {
      return time.getTime() > moscowTime;
    }
    return true;
  };

  const submitDisabled = !selectedItem || isCurrentProjectEmpty;

  return (
    <form className={classes.root} method="post" onSubmit={formState.submitHandler}>
      <>
        <p className={classes.helpText}>{t("components.ParticipationActivityModal.helpText")}</p>
        <Textarea
          className={classes.input}
          name="emails"
          rows={6}
          required
          placeholder={t("components.ParticipationActivityModal.placeholder")}
          onChange={formState.changeHandler}
        />
      </>

      <p className={classes.helpText}>{t("components.ParticipationActivityModal.action.helpText")}</p>
      <Select className={classes.input} name="action" value={formState.values.action} onChange={onActionChange}>
        <option value="deactivate">{t("components.ParticipationActivityModal.action.deactivate")}</option>
        <option value="activate">{t("components.ParticipationActivityModal.action.activate")}</option>
        <option value="delete">{t("components.ParticipationActivityModal.action.delete")}</option>
      </Select>

      <p className={classes.helpText}>{t("components.ParticipationActivityModal.type.helpText.label")}</p>
      <Select
        className={classes.input}
        name="type"
        value={formState.values.type}
        onChange={onTypeChange}
      >
        <option value="Challenge">
          {t("components.AssignModal.type.challenge")}
        </option>
        <option value="Project">
          {t("components.AssignModal.type.сourse")}
        </option>
      </Select>

      {formState.values.action === "activate" && (
        <>
          <div className={classes.helpItem}>
            <p className={classes.helpText}>{t("components.ActivateParticipationModal.helpText.active")}</p>
            <WithTooltip
              className={classes.helpCircleIcon}
              as="span"
              helpText={t("components.ActivateParticipationModal.helpText.timeMsk")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </div>

          <div className="flex items-center gap-4">
            <DatePicker
              className={classes.customDatePicker}
              onChange={(value: Date) => setDeactivateAt(value ?? undefined)}
              selected={deactivateAt}
              minDate={nowDate}
              filterTime={(time) => filterPassedTime(time)}
              placeholderText={t("components.ActivateParticipationModal.datePicker.activeTo")}
              timeCaption={t("components.ActivateParticipationModal.datePicker.time")}
              showTimeSelect
              dateFormat="Pp"
              timeIntervals={15}
              shouldCloseOnSelect={true}
            />
          </div>
        </>
      )}

      <p className={classes.helpText}>
        {type === "Challenge" && t("components.ParticipationActivityModal.type.helpText.challenge")}
        {type === "Project" && t("components.ParticipationActivityModal.type.helpText.project")}
      </p>
      {isCurrentProjectEmpty && (
        <p className={classes.warning}>
          {t("components.ParticipationActivityModal.warning.emptyProject")}
        </p>
      )}

      <div className={classes.bottom}>
        <Select name="id" className={classes.select} value={formState.values.id} onChange={formState.changeHandler}>
          <option value="no">{t("components.AssignModal.no")}</option>
          {currentItems &&
            currentItems.map((item) => (
              <option key={item.id} value={item.id} translate="no">
                {item.name}
              </option>
            ))}
        </Select>

        <Button type="submit" color={submitDisabled ? "secondary" : "success"} disabled={submitDisabled}>
          {t("components.ParticipationActivityModal.submit")}
        </Button>
      </div>
    </form>
  );
}

const ParticipationActivityModal = {
  Header: ParticipationActivityModalHeader,
  Content: ParticipationActivityModalContent,
  fragments: {
    root: gql`
      fragment ParticipationActivityModal on ChallengeProject {
        __typename
        id
      }
    `,
    challenges: gql`
      fragment ParticipationActivityModalChallenges on Challenge {
        id
        name
      }
    `,
    projects: gql`
      fragment ParticipationActivityModalProjects on Project {
        id
        name
      }
    `,
  }
}

export default ParticipationActivityModal;
